import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

declare let gtag: Function;

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private trackingId: string | null = null;

  constructor(private router: Router) {
    // Get tracking ID from environment
    this.trackingId = (environment as any).googleAnalyticsId || null;

    if (this.trackingId) {
      this.loadGoogleAnalyticsScript();
    }
  }

  /**
   * Load Google Analytics script dynamically
   */
  private loadGoogleAnalyticsScript(): void {
    if (document.querySelector(`script[src*="googletagmanager.com"]`)) {
      // Script already loaded
      this.initializeTracking();
      this.trackRouteChanges();
      return;
    }

    // Load Google Analytics script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${this.trackingId}`;
    document.head.appendChild(script1);

    // Initialize gtag
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${this.trackingId}');
    `;
    document.head.appendChild(script2);

    // Wait for script to load, then initialize tracking
    script1.onload = () => {
      this.initializeTracking();
      this.trackRouteChanges();
    };
  }

  /**
   * Initialize Google Analytics
   */
  private initializeTracking(): void {
    if (typeof gtag === 'undefined') {
      return;
    }

    gtag('config', this.trackingId, {
      page_path: window.location.pathname,
    });
  }

  /**
   * Track route changes automatically
   */
  private trackRouteChanges(): void {
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      if (this.trackingId && typeof gtag !== 'undefined') {
        gtag('config', this.trackingId, {
          page_path: event.urlAfterRedirects,
        });
      }
    });
  }

  /**
   * Track a custom event
   */
  trackEvent(eventName: string, eventParams?: { [key: string]: any }): void {
    if (this.trackingId && typeof gtag !== 'undefined') {
      gtag('event', eventName, eventParams);
    }
  }
}
